import React, { useEffect, useRef, useState } from 'react';

import { Alert, Box, Container, Typography, TextField, Button } from '@mui/material';
import { useLocation } from 'react-router-dom';

import contactInquiryLarge from '../assets/images/contact-inquiry-1536.webp';
import contactInquirySmall from '../assets/images/contact-inquiry-768.webp';
import BookCallLink from '../components/BookCallLink';
import ContactTurnstile, { ContactTurnstileHandle } from '../components/ContactTurnstile';
import SectionTitle from '../components/SectionTitle';

const workOptions = [
  'CMS or content platform',
  'Custom application',
  'Integration',
  'Not sure',
] as const;

const whenOptions = ['Now', '1 to 3 months', 'Later'] as const;

const assessmentSubjectSlug = 'cms-workflow-assessment';
const assessmentPrefill = 'Subject: CMS & Workflow Assessment\n\n';

function initialMessageFromSearch(search: string) {
  const params = new URLSearchParams(search);
  return params.get('subject') === assessmentSubjectSlug ? assessmentPrefill : '';
}

function ContactUs() {
  // The Turnstile sitekey is public. Its secret stays in the Worker environment.
  const sitekey = '0x4AAAAAAFl8Ck5gZaziBB56';
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState(() => initialMessageFromSearch(location.search));
  const [work, setWork] = useState('');
  const [when, setWhen] = useState('');
  const [token, setToken] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({ name: false, email: false, message: false });
  const sendingRef = useRef(false);
  const turnstileRef = useRef<ContactTurnstileHandle>(null);
  const nameValid = name.trim().length > 0 && name.trim().length <= 100;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const messageText = message.trim();
  const messageValid =
    messageText.length >= 10 &&
    messageText.length <= 5_000 &&
    messageText !== assessmentPrefill.trim();
  const valid = nameValid && emailValid && messageValid;

  useEffect(() => {
    const prefill = initialMessageFromSearch(location.search);
    if (prefill) {
      setMessage((current) => (current.trim().length === 0 ? prefill : current));
    }
  }, [location.search]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (sendingRef.current || !valid || !token) return;
    sendingRef.current = true;
    setStatus('sending');
    setError('');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          ...(work ? { work } : {}),
          ...(when ? { when } : {}),
          token,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.ok !== true) throw new Error('Delivery failed');
      setName('');
      setEmail('');
      setMessage('');
      setWork('');
      setWhen('');
      setTouched({ name: false, email: false, message: false });
      setStatus('success');
    } catch {
      setStatus('error');
      setError('Your message could not be sent. Please try again.');
    } finally {
      sendingRef.current = false;
      turnstileRef.current?.reset();
      setToken('');
    }
  }

  return (
    <Container>
      <SectionTitle
        first='Contact'
        second='us'
        type='titles'
        component='h1'
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.15fr) minmax(0, 1fr)' },
          alignItems: 'start',
          gap: { xs: 2, md: 4 },
          mb: 3,
        }}
      >
        <Box>
          <Typography variant='body1'>Tell us what needs to work better.</Typography>

          <Typography variant='body2'>
            Describe who uses the system, what is difficult today, and what you would like to
            change. If you have a target date or an existing platform, include that too. You do not
            need a finished specification.
          </Typography>

          <Typography variant='body2'>
            We use your message to discuss fit and possible next steps. Please leave out sensitive
            or confidential information.
          </Typography>

          <Typography
            variant='body2'
            sx={{ mt: 2, mb: 1 }}
          >
            Prefer to talk first? Pick a time for a short introductory call.
          </Typography>
          <BookCallLink />
        </Box>
        <Box
          component='form'
          onSubmit={handleSubmit}
          sx={{
            minWidth: 0,
            '& .MuiInputLabel-root.Mui-error, & .MuiFormLabel-asterisk.Mui-error, & .MuiFormHelperText-root.Mui-error':
              {
                color: '#9D2020',
              },
          }}
        >
          <TextField
            label='Name'
            name='name'
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setStatus('idle');
            }}
            onBlur={() => setTouched((previous) => ({ ...previous, name: true }))}
            error={touched.name && !nameValid}
            helperText={
              touched.name && !nameValid
                ? name.trim().length > 100
                  ? 'Use no more than 100 characters.'
                  : 'Enter your name.'
                : undefined
            }
            required
            disabled={status === 'sending'}
            inputProps={{ maxLength: 100 }}
            autoComplete='name'
            fullWidth
            margin='normal'
          />
          <TextField
            label='Email'
            name='email'
            type='email'
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setStatus('idle');
            }}
            onBlur={() => setTouched((previous) => ({ ...previous, email: true }))}
            error={touched.email && !emailValid}
            helperText={
              touched.email && !emailValid
                ? 'Enter a valid email address, such as name@example.com.'
                : 'Use an address such as name@example.com.'
            }
            required
            disabled={status === 'sending'}
            inputProps={{ maxLength: 254 }}
            autoComplete='email'
            fullWidth
            margin='normal'
          />
          <TextField
            label='Message'
            name='message'
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
              setStatus('idle');
            }}
            onBlur={() => setTouched((previous) => ({ ...previous, message: true }))}
            error={touched.message && !messageValid}
            helperText={
              touched.message && !messageValid
                ? message.trim().length > 5_000
                  ? 'Use no more than 5,000 characters.'
                  : 'Enter at least 10 characters, excluding leading and trailing spaces.'
                : 'Describe your project or current challenge. 10–5,000 characters.'
            }
            required
            disabled={status === 'sending'}
            InputProps={{ inputComponent: 'textarea' }}
            inputProps={{
              minLength: 10,
              maxLength: 5000,
              style: { scrollMarginBlockEnd: '16px' },
              onFocus: (event) =>
                event.currentTarget.scrollIntoView({ block: 'nearest', behavior: 'auto' }),
            }}
            multiline
            rows={4}
            fullWidth
            margin='normal'
          />
          <TextField
            id='contact-work'
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label='What kind of work'
            name='work'
            value={work}
            onChange={(event) => {
              setWork(event.target.value);
              setStatus('idle');
            }}
            helperText='Optional.'
            disabled={status === 'sending'}
            fullWidth
            margin='normal'
          >
            <option value=''>Not specified</option>
            {workOptions.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}
          </TextField>
          <TextField
            id='contact-when'
            select
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
            label='When'
            name='when'
            value={when}
            onChange={(event) => {
              setWhen(event.target.value);
              setStatus('idle');
            }}
            helperText='Optional.'
            disabled={status === 'sending'}
            fullWidth
            margin='normal'
          >
            <option value=''>Not specified</option>
            {whenOptions.map((option) => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            ))}
          </TextField>

          <ContactTurnstile
            ref={turnstileRef}
            sitekey={sitekey}
            onToken={(value) => {
              setToken(value);
              if (value) setError('');
            }}
            onError={() => setError('Verification is unavailable.')}
          />

          <Typography
            variant='body2'
            sx={{ mt: 1, mb: 2 }}
          >
            If verification does not finish,{' '}
            <Box
              component='a'
              href='mailto:info@scaledtech.consulting'
              sx={{ overflowWrap: 'anywhere' }}
            >
              email info@scaledtech.consulting directly
            </Box>
            .
          </Typography>

          {status === 'success' && (
            <Alert
              severity='success'
              role='status'
            >
              Message submitted. Thank you.
            </Alert>
          )}
          {error && (
            <Alert
              severity='error'
              role='alert'
            >
              {error}{' '}
              <Box
                component='a'
                href='mailto:info@scaledtech.consulting'
                sx={{
                  color: 'inherit',
                  fontWeight: 700,
                  textDecoration: 'underline',
                  overflowWrap: 'anywhere',
                }}
              >
                Email info@scaledtech.consulting directly
              </Box>
              .
            </Alert>
          )}

          <Button
            variant='contained'
            color='secondary'
            type='submit'
            disabled={!valid || !token || status === 'sending'}
          >
            {status === 'sending' ? 'Sending…' : 'Send Message'}
          </Button>
        </Box>
        <Box
          component='figure'
          sx={{
            m: 0,
            overflow: 'hidden',
            borderRadius: 3,
            border: '1px solid rgba(11, 37, 69, 0.12)',
            bgcolor: '#F8FAFC',
            gridColumn: { xs: '1', md: '2' },
            gridRow: { md: '1 / span 2' },
          }}
        >
          <img
            src={contactInquiryLarge}
            srcSet={`${contactInquirySmall} 768w, ${contactInquiryLarge} 1536w`}
            sizes='(max-width: 900px) calc(100vw - 48px), 440px'
            width={1536}
            height={1024}
            loading='eager'
            decoding='async'
            alt='Concept illustration of an inquiry moving toward planning and a software response.'
            style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '3 / 2' }}
          />
          <Typography
            component='figcaption'
            variant='caption'
            sx={{ display: 'block', px: 2, py: 1.5, color: '#475569' }}
          >
            From inquiry to next steps
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}

export default ContactUs;
